/**
 * chatConstants — All non-component constants used across chat hooks/context.
 * Keeping these out of ChatContext.jsx keeps that provider thin.
 */

export const MAX_CHATS = 25;
export const MAX_CONTEXT_MESSAGE_CHARS = 8000;
export const MAX_CONTEXT_MESSAGES = 7;

export const DRAFTING_TIMEOUT_MS = 90000;
export const ANALYSIS_JOB_POLL_INTERVALS = [2000, 3000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000];
export const ANALYSIS_JOB_MAX_POLLS = 60;

export const ERROR_MESSAGES = {
  ar: 'حدث خطأ في معالجة طلبك. يرجى المحاولة مرة أخرى.',
  en: 'An error occurred processing your request. Please try again.',
};

export const DOCUMENT_ERROR_MESSAGES = {
  ar: 'حدث خطأ في تحليل المستند. يرجى المحاولة مرة أخرى.',
  en: 'Error analyzing document. Please try again.',
};

export const REQUEST_TIMEOUT_MESSAGES = {
  ar: 'عذراً، لم نتمكن من معالجة طلبك. يرجى المحاولة مرة أخرى.',
  en: 'Sorry, we could not process your request. Please try again.',
};

export const AI_TIMEOUT_MESSAGES = {
  ar: 'عذراً، الخدمة بطيئة حالياً. جرب مرة أخرى.',
  en: 'Sorry, the service is slow right now. Please try again.',
};

export const DELAY_TIMEOUT_MESSAGES = {
  ar: 'عذراً، يبدو أن هناك تأخيراً. يمكنك المحاولة مرة أخرى.',
  en: 'Sorry, there seems to be a delay. You can try again.',
};

export const PDF_TIMEOUT_MESSAGES = {
  ar: 'لم نتمكن من قراءة المستند. جرب ملفاً آخر.',
  en: 'We could not read the document. Try another file.',
};

export const PROGRESS_MESSAGES = {
  ar: {
    stage1: '🔍 جاري تحليل سؤالك...',
    stage2: '📚 جاري البحث في الأنظمة...',
    stage3: '✍️ جاري إعداد الإجابة...',
    long: '⏳ يستغرق الأمر وقتاً أطول من المعتاد...',
  },
  en: {
    stage1: '🔍 Analyzing your question...',
    stage2: '📚 Searching legal systems...',
    stage3: '✍️ Preparing your answer...',
    long: '⏳ Taking longer than usual...',
  },
};

export const DEFAULT_DOCUMENT_PROMPTS = {
  ar: 'راجع هذا المستند',
  en: 'Review this document',
};

export const ANALYSIS_PROGRESS_MESSAGES = {
  pending: { ar: '🔍 جاري بدء تحليل المستند...', en: '🔍 Starting document analysis...' },
  segmenting: { ar: '🔍 جاري تحليل بنية العقد...', en: '🔍 Analyzing contract structure...' },
  clause_segmentation: { ar: '🔍 جاري تحليل بنية العقد...', en: '🔍 Analyzing contract structure...' },
  classifying: { ar: '📋 جاري تصنيف بنود العقد...', en: '📋 Classifying contract clauses...' },
  clause_classification: { ar: '📋 جاري تصنيف بنود العقد...', en: '📋 Classifying contract clauses...' },
  per_clause_review: { ar: '📝 جاري مراجعة كل بند...', en: '📝 Reviewing each clause...' },
  rule_engine: { ar: '⚖️ جاري تطبيق القواعد القانونية...', en: '⚖️ Applying legal rules...' },
  grounding: { ar: '⚖️ جاري التأصيل النظامي...', en: '⚖️ Legal grounding...' },
  legal_grounding: { ar: '⚖️ جاري التأصيل النظامي...', en: '⚖️ Legal grounding...' },
  executive_decision: { ar: '📊 جاري تقييم المخاطر...', en: '📊 Risk assessment...' },
  extraction: { ar: '📋 جاري استخراج الالتزامات والبنود المالية...', en: '📋 Extracting obligations and financial terms...' },
  final_report: { ar: '📄 جاري إعداد التقرير النهائي...', en: '📄 Generating final report...' },
  partial: { ar: '✅ اكتمل تحليل البنود، جاري إعداد التقرير...', en: '✅ Clauses analyzed, generating report...' },
  completed: { ar: '✅ اكتمل التحليل بنجاح', en: '✅ Analysis completed successfully' },
  failed_partial: { ar: '⚠️ اكتمل جزء من التحليل', en: '⚠️ Partial analysis completed' },
  failed: { ar: '❌ تعذر إكمال التحليل', en: '❌ Analysis could not be completed' },
};

export const ANALYSIS_STEPS = [
  { id: 'segmenting', label_ar: 'تحليل بنية العقد', label_en: 'Analyzing structure' },
  { id: 'classifying', label_ar: 'تصنيف البنود', label_en: 'Classifying clauses' },
  { id: 'per_clause_review', label_ar: 'مراجعة البنود', label_en: 'Reviewing clauses' },
  { id: 'extraction', label_ar: 'استخراج الالتزامات والبنود المالية', label_en: 'Extracting obligations & financials' },
  { id: 'grounding', label_ar: 'التأصيل النظامي', label_en: 'Legal grounding' },
  { id: 'executive_decision', label_ar: 'تقييم المخاطر', label_en: 'Risk assessment' },
  { id: 'final_report', label_ar: 'إعداد التقرير', label_en: 'Generating report' },
];

export const STAGE_TO_STEP_INDEX = {
  pending: 0,
  segmenting: 0,
  clause_segmentation: 0,
  classifying: 1,
  clause_classification: 1,
  per_clause_review: 2,
  reviewing: 2,
  extraction: 3,
  rule_engine: 4,
  grounding: 4,
  legal_grounding: 4,
  clause_legal_grounding: 4,
  executive_decision: 5,
  scoring: 5,
  partial: 6,
  final_report: 6,
  generating_display: 6,
  completed: 7,
};

export const DRAFTING_INTENT_KEYWORDS = [
  'صياغة', 'اكتب', 'صغ', 'اتفاقية', 'عدم إفشاء', 'عدم افشاء', 'nda', 'confidentiality agreement',
  'employment contract', 'termination letter', 'resignation letter', 'prepare contract', 'draft contract',
];

export const DRAFTING_FLOW_MARKERS = [
  'لصياغة اتفاقية', 'لصياغة عقد', 'اسم الطرف المفصح', 'اسم الطرف المتلقي', 'مدة السرية',
  'to draft the nda', 'disclosing party', 'receiving party', 'confidentiality term',
  'please provide', 'draft the nda',
];

export const NDA_COLLECTION_PROMPT_MARKERS = [
  'أحتاج المعلومات التالية',
  'لصياغة اتفاقية سرية وعدم إفشاء',
  'اسم الطرف المفصح',
  'اسم الطرف المتلقي',
  'سياق العلاقة',
  'to draft the nda, please provide',
  'disclosing party',
  'receiving party',
];

export const FORCE_IMMEDIATE_DRAFT_PROMPTS = {
  ar: 'أنشئ الآن مسودة اتفاقية سرية وعدم إفشاء مكتملة فوراً بالاعتماد على التفاصيل المتاحة، واستخدم افتراضات قانونية سعودية معقولة لأي عناصر ناقصة دون طلب حقول إضافية.',
  en: 'Generate the NDA draft now using available details and reasonable Saudi-law defaults for missing elements without asking for additional fields.',
};

export const ARTIFACT_CONFIRMATION_MESSAGES = {
  draft: {
    ar: 'تم إعداد المسودة ← اضغط لعرضها',
    en: 'Draft is ready → click to open',
  },
  review: {
    ar: 'تم إعداد تقرير المراجعة ← اضغط لعرضه',
    en: 'Review report is ready → click to open',
  },
};
